import { useState } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Input,
  TextArea,
  Control,
  Label,
  PlusIcon,
} from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { keycloakApi } from '@/api/keycloak';

interface CreateGroupDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateGroupDialog = ({ onClose, onSuccess }: CreateGroupDialogProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { mutate: createGroup, isSubmitting } = useApiMutation<
    { name: string; description: string },
    void
  >({
    mutationFn: async (data) => {
      await keycloakApi.createGroup(data.name, data.description);
    },
    successMessage: 'Group created successfully',
    errorMessage: 'Failed to create group',
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await createGroup({ name: name.trim(), description: description.trim() });
  };

  return (
    <Modal
      title="Create New Group"
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerLeft={
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          icon={<PlusIcon />}
          onClick={handleSubmit}
          disabled={!name.trim() || isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create Group'}
        </Button>
      }
    >
      <ModalContent padded>
        <div className="flex flex-col gap-3">
          <Control hideSpacing="bottom">
            <Label htmlFor="group-name">Group Name</Label>
            <Input
              id="group-name"
              placeholder="e.g., Engineering Team"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="group-desc">Description (Optional)</Label>
            <TextArea
              id="group-desc"
              rows={3}
              placeholder="Describe the purpose of this group..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Control>

          <p className="text-xs text-color-tertiary">
            Groups organize members. Permissions are granted per resource (e.g.
            per shared file or email), not on the group itself.
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default CreateGroupDialog;
